// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

/**
 * GnuCash XML format parser and generator.
 *
 * Parser: browser DOMParser, namespace-aware queries via getElementsByTagNameNS.
 * Generator: string building with proper XML escaping.
 *
 * Supports gzip-compressed .gnucash files (default GnuCash format).
 * Detection: checks for gzip magic bytes 0x1f 0x8b at the start of the file.
 */

import type { LedgerEvent, AccountType, ReconcileState, SplitTemplate } from '../types.js';

// ---------------------------------------------------------------------------
// GnuCash XML namespaces
// ---------------------------------------------------------------------------

const NS_GNC = 'http://www.gnucash.org/XML/gnc';
const NS_ACT = 'http://www.gnucash.org/XML/act';
const NS_TRN = 'http://www.gnucash.org/XML/trn';
const NS_SPLIT = 'http://www.gnucash.org/XML/split';
const NS_CMDTY = 'http://www.gnucash.org/XML/cmdty';
const NS_TS = 'http://www.gnucash.org/XML/ts';
const NS_SX = 'http://www.gnucash.org/XML/sx';
const NS_PRICE = 'http://www.gnucash.org/XML/price';
const NS_FS = 'http://www.gnucash.org/XML/fs';
const NS_REC = 'http://www.gnucash.org/XML/recurrence';

// ---------------------------------------------------------------------------
// Account type maps — GnuCash type strings ↔ our AccountType
// ---------------------------------------------------------------------------

const GNUCASH_TYPE_TO_OURS: Record<string, AccountType> = {
  ROOT: 'ROOT',
  ASSET: 'ASSET',
  BANK: 'BANK',
  CASH: 'CASH',
  CREDIT: 'CREDIT_CARD',
  EQUITY: 'EQUITY',
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  LIABILITY: 'LIABILITY',
  MUTUAL: 'MUTUAL_FUND',
  STOCK: 'STOCK',
  RECEIVABLE: 'ACCOUNTS_RECEIVABLE',
  PAYABLE: 'ACCOUNTS_PAYABLE',
  TRADING: 'ASSET',
};

const OURS_TO_GNUCASH: Record<AccountType, string> = {
  ROOT: 'ROOT',
  ASSET: 'ASSET',
  BANK: 'BANK',
  CASH: 'CASH',
  CREDIT_CARD: 'CREDIT',
  EQUITY: 'EQUITY',
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  LIABILITY: 'LIABILITY',
  MUTUAL_FUND: 'MUTUAL',
  STOCK: 'STOCK',
  ACCOUNTS_RECEIVABLE: 'RECEIVABLE',
  ACCOUNTS_PAYABLE: 'PAYABLE',
  // Legacy types
  CREDIT: 'CREDIT',
  INVESTMENT: 'STOCK',
  RECEIVABLE: 'RECEIVABLE',
  PAYABLE: 'PAYABLE',
  TRADING: 'TRADING',
};

// ---------------------------------------------------------------------------
// Parser helpers
// ---------------------------------------------------------------------------

function firstEl(parent: Element, ns: string, localName: string): Element | null {
  return parent.getElementsByTagNameNS(ns, localName).item(0);
}

function firstText(parent: Element, ns: string, localName: string): string {
  return firstEl(parent, ns, localName)?.textContent?.trim() ?? '';
}

function parseFraction(s: string): number {
  const parts = s.split('/', 2);
  if (parts.length === 2) {
    const num = Number(parts[0]);
    const den = Number(parts[1]);
    if (den === 0) return 0;
    return Math.round((num / den) * 100);
  }
  return Math.round(Number(s) * 100);
}

function makeLedgerEvent(seq: number, type: string, payload: Record<string, unknown>): LedgerEvent {
  const id = crypto.randomUUID();
  const timestamp = new Date().toISOString();
  const checksumInput = `${id}|${seq}|${type}|${timestamp}|${JSON.stringify(payload)}`;
  return {
    id,
    sequence: seq,
    type: type as LedgerEvent['type'],
    timestamp,
    payload: payload as unknown as LedgerEvent['payload'],
    checksum: checksumInput,
  };
}

// ---------------------------------------------------------------------------
// Gzip decompression/compression for native .gnucash files
// ---------------------------------------------------------------------------

async function decompressGzip(bytes: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();
  void writer.write(bytes as Uint8Array<ArrayBuffer>);
  void writer.close();
  const chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array);
  }
  const totalLen = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

async function compressGzip(bytes: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  const reader = cs.readable.getReader();
  void writer.write(bytes as Uint8Array<ArrayBuffer>);
  void writer.close();
  const chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array);
  }
  const totalLen = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out as Uint8Array<ArrayBuffer>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function parseGnuCashFile(data: ArrayBuffer): Promise<LedgerEvent[]> {
  let bytes = new Uint8Array(data);
  // Detect gzip by magic bytes 0x1f 0x8b
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    bytes = await decompressGzip(bytes);
  }
  return parseGnuCashXml(new TextDecoder().decode(bytes));
}

export async function generateGnuCashFile(events: LedgerEvent[]): Promise<Uint8Array> {
  const xmlBytes = new TextEncoder().encode(generateGnuCashXml(events));
  return compressGzip(xmlBytes);
}

export function parseGnuCashXml(xmlText: string): LedgerEvent[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');

  const parseErr = doc.querySelector('parsererror');
  if (parseErr) {
    throw new Error(`GnuCash XML parse error: ${parseErr.textContent?.slice(0, 200) ?? 'unknown'}`);
  }

  const events: LedgerEvent[] = [];
  let seq = 1;

  // --- Accounts ---
  for (const el of Array.from(doc.getElementsByTagNameNS(NS_GNC, 'account'))) {
    const accountId = firstText(el, NS_ACT, 'id') || crypto.randomUUID();
    const name = firstText(el, NS_ACT, 'name');
    const rawType = firstText(el, NS_ACT, 'type');
    const type: AccountType = GNUCASH_TYPE_TO_OURS[rawType] ?? 'ASSET';
    const description = firstText(el, NS_ACT, 'description');
    const code = firstText(el, NS_ACT, 'code');
    const parentId = firstEl(el, NS_ACT, 'parent')?.textContent?.trim() ?? null;
    const placeholder = firstText(el, NS_ACT, 'placeholder') === 'true';
    const hidden = firstText(el, NS_ACT, 'hidden') === 'true';

    const cmdtyEl = firstEl(el, NS_ACT, 'commodity');
    const commodityId = cmdtyEl ? (firstEl(cmdtyEl, NS_CMDTY, 'id')?.textContent?.trim() ?? 'USD') : 'USD';
    const commoditySpace = cmdtyEl ? (firstEl(cmdtyEl, NS_CMDTY, 'space')?.textContent?.trim() ?? 'ISO4217') : 'ISO4217';

    const currency = commoditySpace === 'ISO4217' ? commodityId : 'USD';
    const commodity = commoditySpace !== 'ISO4217' && commodityId
      ? { ticker: commodityId, exchange: commoditySpace, fullName: commodityId }
      : undefined;

    events.push(makeLedgerEvent(seq++, 'AccountCreated', {
      accountId,
      name,
      type,
      currency,
      parentId: parentId || null,
      description,
      code: code || undefined,
      hidden: hidden || undefined,
      placeholder,
      commodity,
    }));
  }

  // --- Transactions ---
  for (const el of Array.from(doc.getElementsByTagNameNS(NS_GNC, 'transaction'))) {
    const transactionId = firstText(el, NS_TRN, 'id') || crypto.randomUUID();
    const description = firstText(el, NS_TRN, 'description');
    const num = firstText(el, NS_TRN, 'num');

    const datePostedEl = firstEl(el, NS_TRN, 'date-posted');
    const dateStr = datePostedEl
      ? (firstEl(datePostedEl, NS_TS, 'date')?.textContent?.slice(0, 10) ?? '')
      : '';

    const dateEnteredEl = firstEl(el, NS_TRN, 'date-entered');
    const dateEntered = dateEnteredEl
      ? (firstEl(dateEnteredEl, NS_TS, 'date')?.textContent?.trim() ?? dateStr)
      : dateStr;

    const notes = firstEl(el, NS_TRN, 'notes')?.textContent?.trim() ?? '';

    const txCmdtyEl = firstEl(el, NS_TRN, 'currency');
    const currency = txCmdtyEl
      ? (firstEl(txCmdtyEl, NS_CMDTY, 'id')?.textContent?.trim() ?? 'USD')
      : 'USD';

    const splits = [];
    for (const s of Array.from(el.getElementsByTagNameNS(NS_TRN, 'split'))) {
      const splitId = firstText(s, NS_SPLIT, 'id') || crypto.randomUUID();
      const accountId = firstText(s, NS_SPLIT, 'account');
      const valueStr = firstText(s, NS_SPLIT, 'value') || '0/1';
      const quantityStr = firstText(s, NS_SPLIT, 'quantity') || '0/1';
      const reconcileState = (firstText(s, NS_SPLIT, 'reconciled-state') || 'n') as ReconcileState;
      const reconcileDateRaw = firstText(s, NS_SPLIT, 'reconcile-date');
      const reconcileDate = reconcileDateRaw
        ? (reconcileDateRaw.slice(0, 10) || null)
        : null;
      const memo = firstText(s, NS_SPLIT, 'memo');
      const action = firstText(s, NS_SPLIT, 'action');
      splits.push({
        splitId,
        accountId,
        amount: parseFraction(valueStr),
        quantity: parseFraction(quantityStr),
        reconcileState,
        reconcileDate,
        memo,
        action,
      });
    }

    events.push(makeLedgerEvent(seq++, 'TransactionPosted', {
      transactionId,
      date: dateStr,
      dateEntered,
      description,
      num: num || undefined,
      notes,
      currency,
      splits,
    }));
  }

  // --- Price database ---
  for (const el of Array.from(doc.getElementsByTagNameNS(NS_PRICE, 'price'))) {
    const priceId = firstText(el, NS_PRICE, 'id') || crypto.randomUUID();
    const commodityEl = firstEl(el, NS_PRICE, 'commodity');
    const commodityId = commodityEl
      ? (firstEl(commodityEl, NS_CMDTY, 'id')?.textContent?.trim() ?? '')
      : '';
    const currencyEl = firstEl(el, NS_PRICE, 'currency');
    const currency = currencyEl
      ? (firstEl(currencyEl, NS_CMDTY, 'id')?.textContent?.trim() ?? 'USD')
      : 'USD';
    const timeEl = firstEl(el, NS_PRICE, 'time');
    const date = timeEl
      ? (firstEl(timeEl, NS_TS, 'date')?.textContent?.slice(0, 10) ?? '')
      : '';
    const source = firstText(el, NS_PRICE, 'source') || 'unknown';
    const type = (firstText(el, NS_PRICE, 'type') || 'unknown') as
      'bid' | 'ask' | 'last' | 'nav' | 'transaction' | 'unknown';
    const valueStr = firstText(el, NS_PRICE, 'value') || '0/1';

    if (commodityId) {
      events.push(makeLedgerEvent(seq++, 'PriceAdded', {
        priceId,
        commodityId,
        currency,
        date,
        source,
        type,
        value: parseFraction(valueStr),
      }));
    }
  }

  // --- Template transactions (used by scheduled transactions) ---
  // Map: template account GUID → parsed template data (keyed by split account parent)
  const accountParents = new Map<string, string | null>();
  for (const ev of events) {
    if (ev.type === 'AccountCreated') {
      const p = ev.payload as { accountId: string; parentId: string | null };
      accountParents.set(p.accountId, p.parentId);
    }
  }
  interface TemplateTxData { description: string; notes: string; currency: string; splits: SplitTemplate[] }
  const templateByParentAcct = new Map<string, TemplateTxData>();
  const templateTxsEl = doc.getElementsByTagNameNS(NS_GNC, 'template-transactions').item(0);
  if (templateTxsEl) {
    for (const txEl of Array.from(templateTxsEl.getElementsByTagNameNS(NS_GNC, 'transaction'))) {
      const desc = firstText(txEl, NS_TRN, 'description');
      const notes = firstEl(txEl, NS_TRN, 'notes')?.textContent?.trim() ?? '';
      const txCmdtyEl = firstEl(txEl, NS_TRN, 'currency');
      const currency = txCmdtyEl ? (firstEl(txCmdtyEl, NS_CMDTY, 'id')?.textContent?.trim() ?? 'USD') : 'USD';
      const tmplSplits: SplitTemplate[] = [];
      const splitAcctIds: string[] = [];
      for (const s of Array.from(txEl.getElementsByTagNameNS(NS_TRN, 'split'))) {
        const splitId = firstText(s, NS_SPLIT, 'id') || crypto.randomUUID();
        const accountId = firstText(s, NS_SPLIT, 'account');
        const valueStr = firstText(s, NS_SPLIT, 'value') || '0/1';
        const quantityStr = firstText(s, NS_SPLIT, 'quantity') || '0/1';
        const memo = firstText(s, NS_SPLIT, 'memo');
        const action = firstText(s, NS_SPLIT, 'action');
        tmplSplits.push({ splitId, accountId, amount: parseFraction(valueStr), quantity: parseFraction(quantityStr), memo, action });
        splitAcctIds.push(accountId);
      }
      // Index by the parent of each split's account so sx:templ-acct can find this template
      for (const acctId of splitAcctIds) {
        const parentId = accountParents.get(acctId);
        if (parentId) templateByParentAcct.set(parentId, { description: desc, notes, currency, splits: tmplSplits });
      }
    }
  }

  // --- Scheduled transactions ---
  for (const el of Array.from(doc.getElementsByTagNameNS(NS_GNC, 'schedxaction'))) {
    const stId = firstText(el, NS_SX, 'id') || crypto.randomUUID();
    const name = firstText(el, NS_SX, 'name');
    const enabled = firstText(el, NS_SX, 'enabled') !== 'n';
    const autoCreate = firstText(el, NS_SX, 'autoCreate') === 'y';

    // GnuCash stores dates under <gdate> sub-elements; textContent captures them
    const startEl = firstEl(el, NS_SX, 'start');
    let startDate = startEl?.textContent?.trim() ?? '';
    if (startDate.length > 10) startDate = startDate.slice(0, 10);
    if (!startDate) startDate = new Date().toISOString().slice(0, 10);

    // Frequency: prefer newer <sx:schedule>/<gnc:recurrence> format over old <sx:freqspec>
    const scheduleEl = firstEl(el, NS_SX, 'schedule');
    const recurrenceEl = scheduleEl ? firstEl(scheduleEl, NS_GNC, 'recurrence') : null;
    const freqSpec = firstEl(el, NS_SX, 'freqspec');

    let freqType: 'once' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' = 'monthly';
    let interval = 1;

    if (recurrenceEl) {
      const mult = parseInt(firstText(recurrenceEl, NS_REC, 'mult') || '1', 10);
      const periodType = firstText(recurrenceEl, NS_REC, 'period_type');
      interval = isNaN(mult) || mult <= 0 ? 1 : mult;
      if (periodType === 'year') freqType = 'yearly';
      else if (periodType === 'month') freqType = 'monthly';
      else if (periodType === 'week') {
        if (interval === 2) { freqType = 'biweekly'; interval = 1; }
        else freqType = 'weekly';
      } else if (periodType === 'day') freqType = 'daily';
    } else if (freqSpec) {
      const readInterval = (el2: Element | null) =>
        el2 ? (parseInt(firstText(el2, NS_FS, 'interval') || '1', 10) || 1) : 1;
      if (firstEl(freqSpec, NS_FS, 'yearly')) {
        freqType = 'yearly'; interval = readInterval(firstEl(freqSpec, NS_FS, 'yearly'));
      } else if (firstEl(freqSpec, NS_FS, 'quarterly')) {
        freqType = 'quarterly';
      } else if (firstEl(freqSpec, NS_FS, 'weekly')) {
        freqType = 'weekly'; interval = readInterval(firstEl(freqSpec, NS_FS, 'weekly'));
      } else if (firstEl(freqSpec, NS_FS, 'daily')) {
        freqType = 'daily'; interval = readInterval(firstEl(freqSpec, NS_FS, 'daily'));
      } else {
        freqType = 'monthly'; interval = readInterval(firstEl(freqSpec, NS_FS, 'monthly'));
      }
    }

    // Link to template transaction via sx:templ-acct
    const tmplAcctId = firstText(el, NS_SX, 'templ-acct');
    const templateData = templateByParentAcct.get(tmplAcctId);

    events.push(makeLedgerEvent(seq++, 'ScheduledTransactionCreated', {
      scheduledTransactionId: stId,
      name,
      enabled,
      autoCreate,
      autoCreateDaysInAdvance: 0,
      advanceReminderDays: 0,
      templateTransaction: {
        description: templateData?.description ?? name,
        num: '',
        notes: templateData?.notes ?? '',
        currency: templateData?.currency ?? 'USD',
        splits: templateData?.splits ?? [],
      },
      frequency: {
        type: freqType,
        interval,
        startDate,
        endDate: null,
        lastOccurrence: null,
        nextOccurrence: null,
      },
    }));
  }

  return events;
}

// ---------------------------------------------------------------------------
// Generate GnuCash XML from events
// ---------------------------------------------------------------------------

function xmlEsc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface AccountState {
  id: string;
  name: string;
  type: AccountType;
  currency: string;
  parentId: string | null;
  description: string;
  code: string;
  hidden: boolean;
  placeholder: boolean;
}

interface SplitState {
  splitId: string;
  accountId: string;
  amount: number;
  quantity: number;
  reconcileState: ReconcileState;
  reconcileDate: string | null;
  memo: string;
  action: string;
}

interface TxState {
  transactionId: string;
  date: string;
  dateEntered: string;
  description: string;
  num: string;
  notes: string;
  currency: string;
  splits: SplitState[];
}

export function generateGnuCashXml(events: LedgerEvent[]): string {
  const accounts = new Map<string, AccountState>();
  const transactions = new Map<string, TxState>();
  const voided = new Set<string>();
  const deleted = new Set<string>();

  for (const ev of events) {
    const p = ev.payload as unknown as Record<string, unknown>;

    switch (ev.type) {
      case 'AccountCreated':
        accounts.set(p['accountId'] as string, {
          id: p['accountId'] as string,
          name: p['name'] as string,
          type: p['type'] as AccountType,
          currency: (p['currency'] as string) ?? 'USD',
          parentId: (p['parentId'] as string | null) ?? null,
          description: (p['description'] as string) ?? '',
          code: (p['code'] as string) ?? '',
          hidden: (p['hidden'] as boolean) ?? false,
          placeholder: (p['placeholder'] as boolean) ?? false,
        });
        break;

      case 'AccountUpdated': {
        const a = accounts.get(p['accountId'] as string);
        if (a) {
          if ('name' in p) a.name = p['name'] as string;
          if ('description' in p) a.description = p['description'] as string;
          if ('code' in p) a.code = (p['code'] as string) ?? '';
          if ('hidden' in p) a.hidden = (p['hidden'] as boolean) ?? false;
          if ('placeholder' in p) a.placeholder = (p['placeholder'] as boolean) ?? false;
          if ('parentId' in p) a.parentId = p['parentId'] as string | null;
        }
        break;
      }

      case 'AccountDeleted':
        accounts.delete(p['accountId'] as string);
        break;

      case 'TransactionPosted':
        transactions.set(p['transactionId'] as string, {
          transactionId: p['transactionId'] as string,
          date: p['date'] as string,
          dateEntered: (p['dateEntered'] as string) ?? (p['date'] as string),
          description: p['description'] as string,
          num: (p['num'] as string) ?? '',
          notes: (p['notes'] as string) ?? '',
          currency: (p['currency'] as string) ?? 'USD',
          splits: (p['splits'] as SplitState[]) ?? [],
        });
        break;

      case 'TransactionVoided':
        voided.add(p['transactionId'] as string);
        break;

      case 'TransactionDeleted':
        deleted.add(p['transactionId'] as string);
        break;

      case 'ReconciliationPosted':
      case 'ReconciliationCompleted': {
        const reconciledIds = new Set(p['reconciledSplitIds'] as string[]);
        for (const tx of transactions.values()) {
          for (const s of tx.splits) {
            if (s.accountId === p['accountId'] && reconciledIds.has(s.splitId)) {
              s.reconcileState = 'y';
              s.reconcileDate = p['statementDate'] as string;
            }
          }
        }
        break;
      }

      case 'SplitReconciled': {
        const tx = transactions.get(p['transactionId'] as string);
        if (tx) {
          const s = tx.splits.find((sp) => sp.splitId === p['splitId']);
          if (s) {
            s.reconcileState = p['reconcileState'] as ReconcileState;
            s.reconcileDate = (p['reconcileDate'] as string | null) ?? null;
          }
        }
        break;
      }
    }
  }

  const lines: string[] = [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<gnc-v2' +
      ' xmlns:gnc="http://www.gnucash.org/XML/gnc"' +
      ' xmlns:act="http://www.gnucash.org/XML/act"' +
      ' xmlns:trn="http://www.gnucash.org/XML/trn"' +
      ' xmlns:split="http://www.gnucash.org/XML/split"' +
      ' xmlns:cmdty="http://www.gnucash.org/XML/cmdty"' +
      ' xmlns:ts="http://www.gnucash.org/XML/ts"' +
      ' xmlns:cd="http://www.gnucash.org/XML/cd">',
    '  <gnc:count-data cd:type="book">1</gnc:count-data>',
    '  <gnc:book version="2.0.0">',
  ];

  for (const [acctId, acct] of accounts) {
    const gnucashType = OURS_TO_GNUCASH[acct.type] ?? 'ASSET';
    lines.push('    <gnc:account version="2.0.0">');
    lines.push(`      <act:name>${xmlEsc(acct.name)}</act:name>`);
    lines.push(`      <act:id type="guid">${xmlEsc(acctId)}</act:id>`);
    lines.push(`      <act:type>${gnucashType}</act:type>`);
    lines.push('      <act:commodity>');
    lines.push('        <cmdty:space>ISO4217</cmdty:space>');
    lines.push(`        <cmdty:id>${xmlEsc(acct.currency)}</cmdty:id>`);
    lines.push('      </act:commodity>');
    if (acct.parentId) {
      lines.push(`      <act:parent type="guid">${xmlEsc(acct.parentId)}</act:parent>`);
    }
    if (acct.description) {
      lines.push(`      <act:description>${xmlEsc(acct.description)}</act:description>`);
    }
    if (acct.code) {
      lines.push(`      <act:code>${xmlEsc(acct.code)}</act:code>`);
    }
    if (acct.placeholder) {
      lines.push('      <act:placeholder>true</act:placeholder>');
    }
    if (acct.hidden) {
      lines.push('      <act:hidden>true</act:hidden>');
    }
    lines.push('    </gnc:account>');
  }

  for (const [txId, tx] of transactions) {
    if (voided.has(txId) || deleted.has(txId)) continue;
    lines.push('    <gnc:transaction version="2.0.0">');
    lines.push('      <trn:currency>');
    lines.push('        <cmdty:space>ISO4217</cmdty:space>');
    lines.push(`        <cmdty:id>${xmlEsc(tx.currency)}</cmdty:id>`);
    lines.push('      </trn:currency>');
    lines.push(`      <trn:id type="guid">${xmlEsc(txId)}</trn:id>`);
    if (tx.num) {
      lines.push(`      <trn:num>${xmlEsc(tx.num)}</trn:num>`);
    }
    lines.push('      <trn:date-posted>');
    lines.push(`        <ts:date>${xmlEsc(tx.date)} 00:00:00 +0000</ts:date>`);
    lines.push('      </trn:date-posted>');
    lines.push('      <trn:date-entered>');
    lines.push(`        <ts:date>${xmlEsc(tx.dateEntered || tx.date)} 00:00:00 +0000</ts:date>`);
    lines.push('      </trn:date-entered>');
    lines.push(`      <trn:description>${xmlEsc(tx.description)}</trn:description>`);
    if (tx.notes) {
      lines.push(`      <trn:notes>${xmlEsc(tx.notes)}</trn:notes>`);
    }
    lines.push('      <trn:splits>');
    for (const s of tx.splits) {
      lines.push('        <trn:split>');
      lines.push(`          <split:id type="guid">${xmlEsc(s.splitId)}</split:id>`);
      if (s.memo) lines.push(`          <split:memo>${xmlEsc(s.memo)}</split:memo>`);
      if (s.action) lines.push(`          <split:action>${xmlEsc(s.action)}</split:action>`);
      lines.push(`          <split:reconciled-state>${s.reconcileState}</split:reconciled-state>`);
      if (s.reconcileDate && s.reconcileState === 'y') {
        lines.push('          <split:reconcile-date>');
        lines.push(`            <ts:date>${xmlEsc(s.reconcileDate)} 00:00:00 +0000</ts:date>`);
        lines.push('          </split:reconcile-date>');
      }
      lines.push(`          <split:value>${s.amount}/100</split:value>`);
      lines.push(`          <split:quantity>${s.quantity}/100</split:quantity>`);
      lines.push(`          <split:account type="guid">${xmlEsc(s.accountId)}</split:account>`);
      lines.push('        </trn:split>');
    }
    lines.push('      </trn:splits>');
    lines.push('    </gnc:transaction>');
  }

  lines.push('  </gnc:book>');
  lines.push('</gnc-v2>');
  return lines.join('\n');
}
